import { Button, cn, plainMarkup } from '@patchbay/ui'
import * as React from 'react'
import { formatWhen } from '../lib/display-time'

export const NO_CASE_SUMMARY = 'No summary yet: Riley builds it on the next pass'

export interface StructuredCaseSummary {
  headline: string
  problem: string
  tried: string[]
  status: string
  waitingOn: string
  next: string
  asOf: string
}

export interface WireCaseSummary {
  headline: string
  problem: string
  tried: string[]
  status: string
  waiting_on: string
  next: string
  as_of: string
}

export function structuredCaseSummary(raw: WireCaseSummary | null | undefined): StructuredCaseSummary | undefined {
  if (!raw?.headline) return undefined
  return {
    headline: raw.headline,
    problem: raw.problem,
    tried: raw.tried,
    status: raw.status,
    waitingOn: raw.waiting_on,
    next: raw.next,
    asOf: raw.as_of,
  }
}

/** Compact relative freshness for a generated staff summary: "updated 2 h ago", never a verification warning. */
export function caseSummaryFreshness(asOf: string, now = Date.now()): string | null {
  const at = Date.parse(asOf)
  if (!Number.isFinite(at)) return null
  const seconds = Math.max(0, Math.floor((now - at) / 1000))
  if (seconds < 60) return 'updated just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `updated ${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `updated ${hours} h ago`
  return `updated ${Math.floor(hours / 24)} d ago`
}

function SummaryField({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-2xs font-medium uppercase tracking-wide text-subtle">{label}</dt>
      <dd className="text-foreground [overflow-wrap:anywhere]">{plainMarkup(value)}</dd>
    </div>
  )
}

/** The one structured summary renderer used by the case rail, header expansion and phone surfaces (#382). */
export function CaseSummaryContent({
  summary,
  showHeadline = true,
  className,
}: {
  summary: StructuredCaseSummary
  showHeadline?: boolean
  className?: string
}) {
  const freshness = caseSummaryFreshness(summary.asOf)
  return (
    <div className={cn('space-y-2', className)} data-slot="structured-case-summary">
      {showHeadline ? <p className="font-medium text-foreground [overflow-wrap:anywhere]">{plainMarkup(summary.headline)}</p> : null}
      <dl className="grid gap-2">
        <SummaryField label="What the issue is" value={summary.problem} />
        {summary.tried.length ? (
          <div>
            <dt className="text-2xs font-medium uppercase tracking-wide text-subtle">What's been tried</dt>
            <dd className="text-foreground">
              <ul className="list-disc space-y-0.5 pl-4">
                {summary.tried.map((item) => (
                  <li key={item} className="[overflow-wrap:anywhere]">
                    {plainMarkup(item)}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ) : null}
        <SummaryField label="Where it stands" value={summary.status} />
        <SummaryField label="Who it's waiting on" value={summary.waitingOn} />
        <SummaryField label="Next" value={summary.next} />
      </dl>
      {freshness ? (
        <p className="text-xs text-subtle" title={formatWhen(summary.asOf, { style: 'absolute' })}>
          {freshness}
        </p>
      ) : null}
    </div>
  )
}

/** An explicit null state. The action uses Riley's read-only summary route and never writes to Desk. */
export function MissingCaseSummary({ onBuild, className }: { onBuild?: () => Promise<void>; className?: string }) {
  const [building, setBuilding] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const build = async () => {
    if (!onBuild || building) return
    setBuilding(true)
    setError(null)
    try {
      await onBuild()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Riley could not build the summary. Try again.')
    } finally {
      setBuilding(false)
    }
  }
  return (
    <div className={cn('space-y-1.5', className)} data-slot="missing-case-summary">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">{NO_CASE_SUMMARY}</p>
        {onBuild ? (
          <Button type="button" size="sm" variant="outline" disabled={building} onClick={() => void build()}>
            {building ? 'Building…' : 'Build now'}
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
